import { captureException } from '@sentry/react';
// eslint-disable-next-line no-restricted-imports -- Retained until the Townsquare router facade migration is completed; see VOLTPLAT-330.
import { RouteResource } from 'react-resource-router';

import { resolvedActivityProvider } from '@townsquare/activity-provider/provider';
import { getWorkedOnAndViewedItemsResults } from '@townsquare/activity-provider/types';
import { resolvedAvailableSitesProvider } from '@townsquare/available-sites-provider/provider';
import type { getAvailableProductsResult } from '@townsquare/available-sites-provider/types';
import { resolvedProductConfigurationProvider } from '@townsquare/product-configuration-provider';
import type { getProductConfigurationResult } from '@townsquare/product-configuration-provider/types';
import { createResource } from '@townsquare/router/resources/createResource';
import type { HomeResourceContext } from '@townsquare/router/types';

import { UNTENANTED_PRODUCTS } from './consts';


export interface AppsResourceData {
	products: getAvailableProductsResult;
	config: getProductConfigurationResult;
	activity: getWorkedOnAndViewedItemsResults;
}

const getSafeWorkedOnAndViewedItems = async (cloudIds: string[]): Promise<getWorkedOnAndViewedItemsResults> => {
	const defaultData: getWorkedOnAndViewedItemsResults = {
		data: { viewed: [], workedOn: [] },
		error: false,
	};

	if (!resolvedActivityProvider) {
		return defaultData;
	}

	return resolvedActivityProvider
		.getWorkedOnAndViewedItemsWithPostFiltering({
			cloudIds: cloudIds.concat(UNTENANTED_PRODUCTS),
		})
		.catch(error => {
			captureException(error);
			return defaultData;
		});
};

export const appsResource: RouteResource<AppsResourceData> = createResource<AppsResourceData>({
	getData: async (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;

		const orgId = context.orgId ?? '';
		const cloudIds = context.collabContext?.orgCloudIds ?? [];

		const [products, config, activity] = await Promise.all([
			resolvedAvailableSitesProvider.getAvailableProducts({ cloudIdsToFilterFor: cloudIds, orgId }),
			resolvedProductConfigurationProvider.getProductConfiguration(),
			getSafeWorkedOnAndViewedItems(cloudIds),
		]);

		return {
			products,
			config,
			activity,
		};
	},
	getKey: (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;

		const orgId = context.orgId ?? '';

		return `apps-resource-key-${orgId}`;
	},
	type: 'APP_RESOURCE',
	maxAge: 60_000 * 30, // 30 minute cache during the SPA session
});
